import { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { useState } from "react";
import { Toast } from "antd-mobile";
import { Column, createRecord, ResultTable, ScanRecord, insertRecordBySeq } from "./ResultTable";
import { useScanQueueConsumer } from "./useScanQueueConsumer";
import { parseException, showError, showRepeatedScanError, parseExceptionMessageKey } from "utils/exceptionUtils";
import { ScanInput } from "./ScanInput";
import { isTrackingNumber, isValidBarCode, showBarCodeError } from "../../../utils/BarCodeUtil";
import { playScanFailedAoSound } from "../../../utils/scanUtils";
import { t } from '../../../i18n';

export interface HandleScanEventResult {
    success: boolean;
}

//修改扫描行，字段的变更
export interface ArrivalRelatedScanComponentRef {
    updateRecord: (trackingNumber: string, updates: Record<string, any>) => void;
    addSuccessRecord: (record: { value1: string; [key: string]: any }) => void;
    moveToSuccess: (trackingNumber: string, extras?: Record<string, any>) => void;
}

type ArrivalScanComponentProps = {
    value1Placeholder: string;
    value1ColumnTitle: string;
    handleScanEvent: (barCode: string) => Promise<HandleScanEventResult>;
    /**
     * 可选，扩展成功记录的额外字段（如 [{ title: 'Type', key: 'type' }]）
     */
    successExtraFields?: { title: string; key: string }[];
}

export const ArrivalRelatedScanComponent = forwardRef<ArrivalRelatedScanComponentRef, ArrivalScanComponentProps>(({value1Placeholder, value1ColumnTitle, handleScanEvent, successExtraFields}, ref) => {
    const [successRecords, setSuccessRecords] = useState<ScanRecord[]>([]);
    const [failedRecords, setFailedRecords] = useState<ScanRecord[]>([]);
    const [value1, setValue1] = useState('');

    const { enqueueScan } = useScanQueueConsumer({
        maxConcurrency: 5,
        onProcess: async (event) => {
            const barCode = event.code;

            // 兼容收起键盘
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }

            // 开始新扫描时，清除之前的弹窗
            Toast.clear();
            setValue1(barCode);

            try {
                const result = await handleScanEvent(barCode);
                if (result.success) {
                    // 动态合并额外字段
                    const extra: Record<string, any> = {};
                    if (successExtraFields) {
                        const resultAny = result as any;
                        successExtraFields.forEach(f => {
                            if (resultAny[f.key] !== undefined) extra[f.key] = resultAny[f.key];
                        });
                    }
                    const newRecord = createRecord({ value1: barCode, ...extra, seq: event.seq });
                    setSuccessRecords(prev => insertRecordBySeq(prev, newRecord));
                }
            } catch (error) {
                const messageKey = parseExceptionMessageKey(error);
                let message: string;

                // 检查是否是自定义的扫描失败错误
                if ((error as any)?.customMessage) {
                    message = (error as any).customMessage;
                    // 不播放错误音效，不显示错误提示，因为详情页已经弹出
                } else if (messageKey === "errors.business.alreadyScanned") {
                    showRepeatedScanError(error, 0);
                    const { message: errorMessage } = parseException(error);
                    message = errorMessage;
                } else {
                    const {title, message: errorMessage} = parseException(error);
                    playScanFailedAoSound();
                    showError({title, message: errorMessage}, 0);
                    message = errorMessage;
                }
                setFailedRecords(prev => insertRecordBySeq(prev, createRecord({value1: barCode, failedReason: message, seq: event.seq})));
            }
        },
    });

    // 组件卸载时清理所有 Toast，避免残留到其他页面
    useEffect(() => {
        return () => {
            Toast.clear();
        };
    }, []);

    // 更新成功记录中的特定字段 - 只更新最新的一条记录
    const updateRecord = (trackingNumber: string, updates: Record<string, any>) => {
        setSuccessRecords(prev => {
            // 找到第一个匹配的记录（最新的记录在数组前面）
            const targetIndex = prev.findIndex(record => record.value1 === trackingNumber);
            if (targetIndex === -1) {
                return prev; // 没找到匹配的记录，不做任何更新
            }

            // 只更新找到的第一条记录
            return prev.map((record, index) =>
                index === targetIndex
                    ? { ...record, ...updates }
                    : record
            );
        });
    };

    // 添加新的成功记录
    const addSuccessRecord = (record: { value1: string; [key: string]: any }) => {
        const newRecord = createRecord(record);
        setSuccessRecords(prev => {
            const newRecords = [newRecord, ...prev];
            return newRecords;
        });
    };

    // 将 failedRecords 中第一条匹配记录移到 successRecords
    const moveToSuccess = (trackingNumber: string, extras?: Record<string, any>) => {
        setFailedRecords(prev => {
            const targetIndex = prev.findIndex(r => r.value1 === trackingNumber);
            if (targetIndex === -1) return prev;
            return prev.filter((_, i) => i !== targetIndex);
        });
        addSuccessRecord({ value1: trackingNumber, ...(extras || {}) });
    };

    // 暴露更新记录和添加记录的函数给父组件
    useImperativeHandle(ref, () => ({
        updateRecord,
        addSuccessRecord,
        moveToSuccess,
    }));

    const onScan = (code: string) => {
        if (isValidBarCode(code)) {
            enqueueScan(code);
        } else {
            showBarCodeError(code);
        }
    };

    // 动态拼接成功列，扩展字段放到最后
    const successColumns: Column[] = [
        { title: value1ColumnTitle, key: 'value1' },
        { title: t('pda.common.time'), key: 'time' },
        ...(successExtraFields || [])
    ];

    const failedColumns = [
        { title: value1ColumnTitle, key: 'value1' },
        { title: t('pda.common.time'), key: 'time' },
        {title: t('pda.common.failedReason'), key: 'failedReason'}
    ];

    return (
        <>
            <ScanInput placeholder={value1Placeholder} value={value1} onScan={onScan}/>

            <ResultTable
                successRecords={successRecords}
                failedRecords={failedRecords}
                successColumns={successColumns}
                failedColumns={failedColumns}
            />
        </>
    );
});
